/**
 * features/finance/poWrite.ts
 * ------------------------------------------------------------------
 * Writing purchase orders and goods-received notes.
 *
 * Both numbers are allocated INSIDE the write transaction, so a failed create
 * cannot burn a PO or GRN number — the same rule the voucher path follows, and
 * for the same reason: a gap in a procurement series is a question an auditor
 * will ask and nobody will be able to answer.
 *
 * What a client may change is deliberately narrow. A DRAFT order is the buyer's
 * to edit freely. Once ISSUED, the supplier has been told, so quantities and
 * prices are frozen here — an issued order changes by being cancelled and
 * re-raised, which leaves both documents visible.
 *
 * A GRN is never edited at all. It is somebody's statement that this much
 * physically arrived on this day; correcting it means recording another receipt
 * (a negative quantity for goods returned), so the delivery history survives.
 * ------------------------------------------------------------------ */
import { collection, doc, getDocs, query, runTransaction, where } from 'firebase/firestore';
import { db, updateDoc } from '@/lib/firebase';
import {
  PO_COLLECTIONS, poCounterId, grnCounterId, formatPoNo, formatGrnNo,
  poLinesTotal, normalisePoLine, matchPurchaseOrder, poStatusFrom,
  type PurchaseOrder, type PoLine, type GoodsReceipt, type GrnLine, type PoStatus,
} from '@/lib/purchaseOrders';
import { isPeriodClosed, periodIdFor } from '@/lib/gl';
import { fetchClosedPeriods } from '@/features/finance/glWrite';
import type { Role } from '@/types';

const now = () => new Date().toISOString();

/**
 * LKC closes a month in the GL itself (financeGlPeriods holds only the months
 * that are CLOSED — absent means open), so an order dated into a shut month is
 * refused by the same fact the posting engine reads. A second, parallel notion
 * of "locked" is how two screens come to disagree about whether August is
 * finished.
 */
async function assertPeriodOpen(dateIso: string, _role?: Role | null): Promise<void> {
  /* fetchClosedPeriods, not a second read of the same collection. It
     filters on status === 'closed'; counting every document instead would
     make this disagree with the journal path the moment the collection
     ever holds a row that is not a closure. One definition of "shut". */
  const closed = await fetchClosedPeriods();
  if (isPeriodClosed(dateIso, closed)) {
    throw new Error(
      `${periodIdFor(dateIso)} is closed. Date the order in an open month, or ask `
      + 'the bursar to reopen the period.',
    );
  }
}


export interface NewPoInput {
  supplierName: string;
  supplierId?: string;
  lines: PoLine[];
  date: string;
  expectedDate?: string;
  notes?: string;
  requisitionId?: string;
  reqNo?: string;
  actor: { uid: string; name?: string; role?: Role | null };
}

/** Raise a draft order. Nothing is committed to the supplier until it is issued. */
export async function createPurchaseOrder(input: NewPoInput): Promise<PurchaseOrder> {
  const lines = input.lines.map(normalisePoLine).filter((l) => l.description.trim() && l.qty > 0);
  if (lines.length === 0) throw new Error('An order needs at least one line with a quantity.');
  if (!input.supplierName.trim()) throw new Error('Name the supplier the order goes to.');

  await assertPeriodOpen(input.date, input.actor.role);
  const year = input.date.slice(0, 4);
  const ref = doc(collection(db, PO_COLLECTIONS.orders));
  const counterRef = doc(db, PO_COLLECTIONS.counters, poCounterId(year));
  const at = now();

  return runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef);
    const seq = (snap.exists() ? Number(snap.data().seq) || 0 : 0) + 1;
    const po: PurchaseOrder = {
      id: ref.id,
      poNo: formatPoNo(seq, year),
      ...(input.requisitionId ? { requisitionId: input.requisitionId } : {}),
      ...(input.reqNo ? { reqNo: input.reqNo } : {}),
      ...(input.supplierId ? { supplierId: input.supplierId } : {}),
      supplierName: input.supplierName.trim(),
      lines,
      total: poLinesTotal(lines),
      fy: `FY${year}`,
      status: 'draft',
      ...(input.expectedDate ? { expectedDate: input.expectedDate } : {}),
      ...(input.notes?.trim() ? { notes: input.notes.trim() } : {}),
      date: input.date,
      createdBy: input.actor.uid,
      ...(input.actor.name ? { createdByName: input.actor.name } : {}),
      createdAt: at,
      updatedAt: at,
    };
    tx.set(counterRef, { seq, updatedAt: at }, { merge: true });
    tx.set(ref, po);
    return po;
  });
}

/** Edit a draft. Refuses anything past draft — see the note at the top. */
export async function updateDraftPo(
  po: PurchaseOrder,
  patch: { lines?: PoLine[]; supplierName?: string; expectedDate?: string; notes?: string },
): Promise<void> {
  if (po.status !== 'draft') {
    throw new Error(`${po.poNo} has been issued to the supplier. Cancel it and raise a new order instead.`);
  }
  const next: Record<string, unknown> = { updatedAt: now() };
  if (patch.lines) {
    const lines = patch.lines.map(normalisePoLine).filter((l) => l.description.trim() && l.qty > 0);
    if (lines.length === 0) throw new Error('An order needs at least one line with a quantity.');
    next.lines = lines;
    next.total = poLinesTotal(lines);
  }
  if (patch.supplierName !== undefined) next.supplierName = patch.supplierName.trim();
  if (patch.expectedDate !== undefined) next.expectedDate = patch.expectedDate;
  if (patch.notes !== undefined) next.notes = patch.notes.trim();
  await updateDoc(doc(db, PO_COLLECTIONS.orders, po.id), next);
}

/** draft -> issued. The point at which the order becomes a commitment. */
export async function issuePo(po: PurchaseOrder): Promise<void> {
  if (po.status !== 'draft') throw new Error(`${po.poNo} is already ${po.status}.`);
  await updateDoc(doc(db, PO_COLLECTIONS.orders, po.id), {
    status: 'issued' satisfies PoStatus,
    issuedAt: now(),
    updatedAt: now(),
  });
}

/**
 * Cancel an order.
 *
 * Blocked once anything has been received: the goods are here, and an order
 * marked cancelled with a delivery against it means the receipt has nothing to
 * belong to. Short-close it instead — the outstanding quantity is written off by
 * closing, which keeps the partial delivery attached to a live document.
 */
export async function cancelPo(po: PurchaseOrder, reason: string, receipts: GoodsReceipt[]): Promise<void> {
  const received = receipts.some((g) => (g.lines ?? []).some((l) => (Number(l.qtyReceived) || 0) !== 0));
  if (received) {
    throw new Error(`${po.poNo} already has goods received against it. Close it short instead of cancelling.`);
  }
  if (!reason.trim()) throw new Error('Give a reason for cancelling the order.');
  await updateDoc(doc(db, PO_COLLECTIONS.orders, po.id), {
    status: 'cancelled' satisfies PoStatus,
    cancelledReason: reason.trim(),
    updatedAt: now(),
  });
}

/** Close an order — including short, when the balance will never arrive. */
export async function closePo(po: PurchaseOrder): Promise<void> {
  await updateDoc(doc(db, PO_COLLECTIONS.orders, po.id), {
    status: 'closed' satisfies PoStatus,
    updatedAt: now(),
  });
}

export async function fetchReceipts(poId: string): Promise<GoodsReceipt[]> {
  const snap = await getDocs(query(collection(db, PO_COLLECTIONS.receipts), where('poId', '==', poId)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as GoodsReceipt);
}

export interface NewGrnInput {
  po: PurchaseOrder;
  lines: GrnLine[];
  receivedDate: string;
  deliveryRef?: string;
  notes?: string;
  actor: { uid: string; name?: string; role?: Role | null };
}

/**
 * Record a delivery, and roll the order's status forward from its receipts.
 *
 * The status is DERIVED (poStatusFrom) rather than set by the person receiving:
 * whoever unpacks the boxes knows what arrived, not whether that completes the
 * order, and asking them to judge it is how orders end up marked "received" with
 * quantities still outstanding.
 */
export async function recordGoodsReceipt(input: NewGrnInput): Promise<GoodsReceipt> {
  const { po } = input;
  if (po.status === 'draft') {
    throw new Error(`${po.poNo} has not been issued yet — issue the order before receiving against it.`);
  }
  if (po.status === 'cancelled') throw new Error(`${po.poNo} was cancelled.`);

  const lines = (input.lines ?? []).filter((l) => (Number(l.qtyReceived) || 0) !== 0);
  if (lines.length === 0) throw new Error('Enter what actually arrived.');
  const bad = lines.find((l) => !po.lines[l.poLineIdx]);
  if (bad) throw new Error('A received line does not correspond to a line on the order.');

  await assertPeriodOpen(input.receivedDate, input.actor.role);

  const year = input.receivedDate.slice(0, 4);
  const ref = doc(collection(db, PO_COLLECTIONS.receipts));
  const counterRef = doc(db, PO_COLLECTIONS.counters, grnCounterId(year));
  const poRef = doc(db, PO_COLLECTIONS.orders, po.id);
  const at = now();

  // Read the existing receipts BEFORE the transaction: a transaction cannot run
  // a query, and the status roll-forward needs the full delivery history. A
  // concurrent second delivery would leave the status one receipt behind, which
  // the next receive corrects — a stale label, never a wrong quantity, because
  // the quantities are always re-derived from the receipt documents themselves.
  const existing = await fetchReceipts(po.id);

  return runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef);
    const seq = (snap.exists() ? Number(snap.data().seq) || 0 : 0) + 1;
    const grn: GoodsReceipt = {
      id: ref.id,
      grnNo: formatGrnNo(seq, year),
      poId: po.id,
      poNo: po.poNo,
      supplierName: po.supplierName,
      lines,
      receivedDate: input.receivedDate,
      receivedBy: input.actor.uid,
      ...(input.actor.name ? { receivedByName: input.actor.name } : {}),
      ...(input.deliveryRef?.trim() ? { deliveryRef: input.deliveryRef.trim() } : {}),
      ...(input.notes?.trim() ? { notes: input.notes.trim() } : {}),
      createdAt: at,
    };

    const match = matchPurchaseOrder(po, [...existing, grn], 0);
    tx.set(counterRef, { seq, updatedAt: at }, { merge: true });
    tx.set(ref, grn);
    tx.update(poRef, { status: poStatusFrom(po.status, match), updatedAt: at });
    return grn;
  });
}

/** Note that a voucher has been raised against this order. */
export async function linkVoucherToPo(poId: string, voucherId: string, existing: string[] = []): Promise<void> {
  if (existing.includes(voucherId)) return;
  await updateDoc(doc(db, PO_COLLECTIONS.orders, poId), {
    voucherIds: [...existing, voucherId],
    updatedAt: now(),
  });
}
